import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

function ProcedureBillModal({ patientData, onClose, image, watermarkImage, footerImage }) {
    const [formData, setFormData] = useState({
        procedure_bill_id: '',
        ipd_bill_id: patientData?.ipd_bill_id || patientData?.opd_id || '',
        ipd_bill_uhid: patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no || '',
        ipd_no: patientData?.ipd_no || patientData?.opd_id || '',
        patient_name: patientData?.ipd_bill_patient_name || patientData?.opd_patient_name || '',
        patient_age: patientData?.ipd_bill_patient_age || patientData?.opd_guardien_age || patientData?.opd_patient_age || '',
        patient_gender: patientData?.ipd_bill_patient_gender || patientData?.opd_patient_gender || '',
        patient_phone: patientData?.ipd_bill_patient_phone || patientData?.opd_patient_mobile || '',
        patient_address: patientData?.ipd_bill_patient_address || patientData?.opd_address || '',
        bill_date: new Date().toISOString().slice(0, 16),
        created_at: '',
        total_amount: 0,
        discount_percent: 0,
        discount_amount: 0,
        final_amount: 0,
        payment_mode: 'Cash',
        remarks: '',
        items: [],
        services: []
    });

    const [searchKey, setSearchKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [isLoadingServices, setIsLoadingServices] = useState(false);
    const [headerImageUrl, setHeaderImageUrl] = useState(image || '');
    const [isLoadingBill, setIsLoadingBill] = useState(false);
    
    const modalBodyRef = useRef(null);
    const scrollPositionRef = useRef(0);

    // ✅ Fetch existing procedure bill if procedure_bill_id exists
    useEffect(() => {
        const fetchExistingBill = async () => {
            const billId = patientData?.procedure_bill_id;
            const uhid = patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no;

            if (!billId && !uhid) {
                console.log('No procedure_bill_id or UHID - creating new bill');
                return;
            }

            setIsLoadingBill(true);
            console.log('📋 Fetching existing procedure bill:', { billId, uhid });

            try {
                const fd = new FormData();
                if (billId) {
                    fd.append('procedure_bill_id', billId);
                } else if (uhid) {
                    fd.append('uhid', uhid);
                }

                const response = await axios.post(
                    AppConst.BASE_URL + AppConst.getProcedureBillUrl,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                console.log('📋 Procedure bill response:', response.data);

                if (response?.data?.status && response.data.bill) {
                    const bill = response.data.bill;
                    const items = response.data.items || [];

                    console.log('✅ Loaded existing bill:', bill);
                    console.log('✅ Loaded items:', items);

                    // Map items to form structure
                    const mappedItems = items.map(item => ({
                        service_id: item.service_id || item.cat_id || '',
                        service_name: item.service_name || item.cat_name || '',
                        qty: Number(item.qty || item.quantity || 1),
                        unit_price: Number(item.unit_price || item.price || 0),
                        discount: Number(item.discount || 0),
                        final_price: Number(item.final_price || item.amount || 0)
                    }));

                    setFormData(prev => ({
                        ...prev,
                        procedure_bill_id: bill.procedure_bill_id || billId,
                        ipd_bill_id: bill.ipd_bill_id || prev.ipd_bill_id,
                        ipd_bill_uhid: bill.ipd_bill_uhid || prev.ipd_bill_uhid,
                        ipd_no: bill.ipd_no || prev.ipd_no,
                        patient_name: bill.patient_name || prev.patient_name,
                        patient_age: bill.patient_age || prev.patient_age,
                        patient_gender: bill.patient_gender || prev.patient_gender,
                        patient_phone: bill.patient_phone || prev.patient_phone,
                        patient_address: bill.patient_address || prev.patient_address,
                        bill_date: bill.bill_date || prev.bill_date,
                        created_at: bill.created_at || bill.bill_date || '',
                        total_amount: Number(bill.total_amount || 0).toFixed(2),
                        discount_percent: Number(bill.discount_percent || 0),
                        discount_amount: Number(bill.discount_amount || 0).toFixed(2),
                        final_amount: Number(bill.final_amount || 0).toFixed(2),
                        payment_mode: bill.payment_mode || 'Cash',
                        remarks: bill.remarks || '',
                        items: mappedItems
                    }));

                    toast.success('Existing procedure bill loaded');
                } else {
                    console.log('⚠️ No existing bill found');
                }
            } catch (err) {
                console.error('❌ Error fetching procedure bill:', err);
                toast.error('Failed to load existing bill');
            } finally {
                setIsLoadingBill(false);
            }
        };

        fetchExistingBill();
    }, [patientData]);

    // ✅ Fetch header image from config
    useEffect(() => {
        const fetchHeaderImage = async () => {
            try {
                if (image) {
                    console.log('Header image from props:', image);
                    setHeaderImageUrl(image);
                    return;
                }

                console.log('Fetching header image from config API...');
                const res = await axios.post(
                    `${AppConst.BASE_URL}${AppConst.getconfig}?config_type=lab`
                );

                if (res.data.status && res.data.header_logo) {
                    console.log('Header image from API:', res.data.header_logo);
                    setHeaderImageUrl(res.data.header_logo);
                } else {
                    console.warn('No header image in config response');
                }
            } catch (err) {
                console.error('Error fetching header image:', err);
            }
        };

        fetchHeaderImage();
    }, [image]);

    // ✅ Prevent mouse wheel from changing number input values
    useEffect(() => {
        const preventWheel = (e) => {
            if (document.activeElement.type === 'number') {
                e.preventDefault();
            }
        };

        document.addEventListener('wheel', preventWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', preventWheel);
        };
    }, []);

    const fetchServices = async (inputValue = '') => {
        setIsLoadingServices(true);
        try {
            const fd = new FormData();
            fd.append('cat_type', 'service');
            fd.append('page_no', 'none');
            fd.append('search_key', inputValue);
            fd.append('par_by', '1');

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getCatListUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status && Array.isArray(response.data.records)) {
                const services = response.data.records;
                
                const options = services.map(srv => ({
                    value: srv.cat_id || srv.id,
                    label: srv.cat_name || srv.name,
                    price: Number(srv.cat_price || srv.price || 0),
                    serviceData: srv
                }));

                setServiceOptions(options);
                setFormData(prev => ({ ...prev, services }));
            }
        } catch (err) {
            console.error('Error fetching services:', err);
        } finally {
            setIsLoadingServices(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleServiceSelect = (selectedOption) => {
        if (!selectedOption) return;

        const service = selectedOption.serviceData;
        const price = Number(service.cat_price || service.price || 0);
        
        const newItem = {
            service_id: service.cat_id || service.id,
            service_name: service.cat_name || service.name,
            qty: 1,
            unit_price: price,
            discount: 0,
            final_price: price
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });

        toast.success(`${newItem.service_name} added`);
    };

    const calculateTotals = (currentData) => {
        const items = currentData.items || [];
        const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
        const discountPercent = Number(currentData.discount_percent || 0);
        const discountAmount = (total * discountPercent) / 100;
        const finalAmount = total - discountAmount;

        return {
            total_amount: total.toFixed(2),
            discount_amount: discountAmount.toFixed(2),
            final_amount: finalAmount.toFixed(2)
        };
    };

    const handleItemChange = (index, field, value) => {
        if (modalBodyRef.current) {
            scrollPositionRef.current = modalBodyRef.current.scrollTop;
        }

        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };

            const item = updatedItems[index];
            const qty = Number(item.qty || 0);
            const unitPrice = Number(item.unit_price || 0);
            const discount = Number(item.discount || 0);
            item.final_price = (qty * unitPrice) - discount;

            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });

        setTimeout(() => {
            if (modalBodyRef.current) {
                modalBodyRef.current.scrollTop = scrollPositionRef.current;
            }
        }, 0);
    };

    const removeItem = (index) => {
        setFormData(prev => {
            const updatedItems = prev.items.filter((_, i) => i !== index);
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });
    };

    const handleDiscountChange = (value) => {
        setFormData(prev => {
            const totals = calculateTotals({ ...prev, discount_percent: value });
            return { ...prev, discount_percent: value, ...totals };
        });
    };

    const handleSave = async () => {
        if (formData.items.length === 0) {
            toast.error('Please add at least one service');
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Save Procedure Bill?',
            text: 'Do you want to save this procedure bill?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel'
        });

        if (!isConfirmed) return;

        setSaving(true);
        Swal.fire({ title: 'Saving...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        try {
            const fd = new FormData();
            Object.keys(formData).forEach(key => {
                if (key !== 'items' && key !== 'services' && key !== 'created_at') {
                    fd.append(key, formData[key] || '');
                }
            });
            fd.append('items', JSON.stringify(formData.items));

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.saveProcedureBillUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (response?.data?.status) {
                toast.success('Procedure bill saved successfully');
                setFormData(prev => ({
                    ...prev,
                    procedure_bill_id: response.data.procedure_bill_id || response.data.id,
                    created_at: response.data.created_at || new Date().toISOString()
                }));
            } else {
                toast.error(response?.data?.message || 'Failed to save');
            }
        } catch (err) {
            Swal.close();
            console.error('Save error:', err);
            toast.error('Failed to save procedure bill');
        } finally {
            setSaving(false);
        }
    };

    const imageUrlToBase64 = async (url) => {
        if (!url) {
            console.error("imageUrlToBase64: Provided URL is empty or invalid.");
            return null;
        }

        console.log("🖼️ Attempting to fetch image from URL:", url);

        try {
            const response = await fetch(url, { cache: 'no-cache' });
            if (!response.ok) {
                console.error(`❌ Failed to fetch image. URL: ${url}, Status: ${response.status} ${response.statusText}`);
                return null;
            }
            
            const blob = await response.blob();
            console.log("📦 Blob type:", blob.type, "Size:", blob.size);
        
            if (!blob.type.startsWith('image/')) {
                console.error(`❌ The URL did not point to an image. URL: ${url}, MIME Type: ${blob.type}`);
                return null;
            }
        
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    console.log("✅ Image successfully converted to base64, length:", reader.result?.length);
                    resolve(reader.result);
                };
                reader.onerror = (error) => {
                    console.error("❌ FileReader error:", error);
                    reject(error);
                };
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("❌ Error fetching or converting image. This might be a CORS issue.", { url, error });
            return null;
        }
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return new Date().toLocaleString();
        
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
                return new Date().toLocaleString();
            }
            
            const day = String(date.getDate()).padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            
            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Date formatting error:', error);
            return new Date().toLocaleString();
        }
    };

    const generatePDF = async () => {
        if (formData.items.length === 0) {
            toast.error('No items to print');
            return;
        }

       

        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = 0;

        const imageToUse = headerImageUrl || image;

        try {
            if (imageToUse) {
                console.log('🔄 Converting image to base64...');
                const headerImageBase64 = await imageUrlToBase64(imageToUse);
                
                if (headerImageBase64) {
                    console.log('✅ Base64 conversion successful!');
                    doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, 80);
                    y = 105; // ✅ INCREASED SPACING: was 90, now 105 (15pt more space)
                    console.log('✅ Header image added, content starts at y =', y);
                } else {
                    y = 40;
                }
            } else {
                y = 40;
            }
        } catch (error) {
            console.error('❌ Header image error:', error);
            y = 40;
        }

        // Title
        doc.setFontSize(16).setFont('helvetica', 'bold');
        doc.text('PROCEDURE BILL', pageWidth / 2, y, { align: 'center' });
        y += 30;

        // Patient Details
        const leftX = 30;
        const rightX = 330;
        const labelOffset = 110;

        const addInfoLine = (label1, value1, label2, value2) => {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold').text(label1, leftX, y);
            doc.setFont('helvetica', 'normal').text(`: ${value1}`, leftX + labelOffset, y);

            if (label2) {
                doc.setFont('helvetica', 'bold').text(label2, rightX, y);
                doc.setFont('helvetica', 'normal').text(`: ${value2}`, rightX + labelOffset, y);
            }
            y += 18;
        };

        const billDateDisplay = formatDateForDisplay(formData.created_at || formData.bill_date);
        
        addInfoLine('Bill No', `PRO-${formData.procedure_bill_id || 'NEW'}`, 'Bill Date', billDateDisplay);
        addInfoLine('UHID', formData.ipd_bill_uhid, 'OPD No', `000${formData.ipd_no}`);
        addInfoLine('Patient Name', formData.patient_name, 'Age/Gender', `${formData.patient_age}/${formData.patient_gender}`);
        addInfoLine('Phone', formData.patient_phone, 'Address', formData.patient_address);

        y += 10;

        // Items Table
        const tableData = formData.items.map((item, index) => [
            index + 1,
            item.service_name,
            item.qty,
            Number(item.unit_price).toFixed(2),
            Number(item.discount).toFixed(2),
            Number(item.final_price).toFixed(2)
        ]);

        autoTable(doc, {
            startY: y,
            head: [['S.No', 'Procedure', 'Qty', 'Unit Price', 'Discount', 'Amount']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [180, 220, 250], textColor: 0, fontStyle: 'bold' },
            styles: { fontSize: 10, cellPadding: 4 },
            columnStyles: {
                0: { halign: 'center', cellWidth: 40 },
                2: { halign: 'center', cellWidth: 40 },
                3: { halign: 'right', cellWidth: 80 },
                4: { halign: 'right', cellWidth: 70 },
                5: { halign: 'right', cellWidth: 80 }
            }
        });

        y = doc.lastAutoTable.finalY + 20;

        // Summary
        const rightAlign = pageWidth - 30;
        const labelX = pageWidth - 180;

        doc.setFontSize(10).setFont('helvetica', 'bold');
        doc.text('Total Amount:', labelX, y);
        doc.text(`${formData.total_amount}`, rightAlign, y, { align: 'right' });
        y += 18;

        doc.text(`Discount (${formData.discount_percent}%):`, labelX, y);
        doc.text(`${formData.discount_amount}`, rightAlign, y, { align: 'right' });
        y += 18;

        doc.setFontSize(12);
        doc.text('Final Amount:', labelX, y);
        doc.text(`${formData.final_amount}`, rightAlign, y, { align: 'right' });
        y += 25;

        // Payment Mode
        doc.setFontSize(9).setFont('helvetica', 'normal');
        doc.text(`Payment Mode: ${formData.payment_mode}`, 30, y);
        y += 15;

        if (formData.remarks) {
            doc.text(`Remarks: ${formData.remarks}`, 30, y);
        }

        // Footer
        doc.setFontSize(9);
        doc.text('Authorized Signature', pageWidth - 30, pageHeight - 30, { align: 'right' });

        console.log('✅ PDF GENERATION COMPLETE');
        //doc.output('dataurlnewwindow');

        const pdfBlob = doc.output('blob');
window.open(URL.createObjectURL(pdfBlob), '_blank');  // ✅ works in jsPDF v3
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '42px',
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#0d6efd' 
                : state.isFocused 
                ? '#e7f1ff' 
                : 'white',
            color: state.isSelected ? 'white' : '#212529',
            cursor: 'pointer',
            padding: '10px 12px'
        })
    };

    const formatOptionLabel = ({ label, price }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{label}</span>
            <span className="badge bg-success ms-2">₹{price.toFixed(2)}</span>
        </div>
    );

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {formData.procedure_bill_id ? 'Edit Procedure Bill' : 'New Procedure Bill'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body" ref={modalBodyRef}>
                        {isLoadingBill ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading procedure bill...</p>
                            </div>
                        ) : (
                            <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Patient Details</h6>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <strong>UHID:</strong> {formData.ipd_bill_uhid}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Name:</strong> {formData.patient_name}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Age/Gender:</strong> {formData.patient_age}/{formData.patient_gender}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>OPD No:</strong> 000{formData.ipd_no}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Search & Select Procedure/Service</label>
                                                    <Select
                                                        options={serviceOptions}
                                                        onChange={handleServiceSelect}
                                                        onInputChange={(inputValue) => {
                                                            if (inputValue.length > 0) {
                                                                fetchServices(inputValue);
                                                            }
                                                        }}
                                                        formatOptionLabel={formatOptionLabel}
                                                        styles={customSelectStyles}
                                                        placeholder="Type to search services..."
                                                        isClearable
                                                        isSearchable
                                                        isLoading={isLoadingServices}
                                                        noOptionsMessage={() => "No services found"}
                                                        loadingMessage={() => "Loading services..."}
                                                        value={null}
                                                        menuPosition="fixed"
                                                        menuPortalTarget={document.body}
                                                    />
                                                    <small className="text-muted">
                                                        Start typing to search, click to add service to the bill
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Procedures/Services</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Service</th>
                                                        <th style={{ width: '80px' }}>Qty</th>
                                                        <th style={{ width: '120px' }}>Unit Price</th>
                                                        <th style={{ width: '120px' }}>Discount</th>
                                                        <th style={{ width: '120px' }}>Final</th>
                                                        <th style={{ width: '80px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center text-muted">
                                                                No services added. Search and select services above.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        formData.items.map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{item.service_name}</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.qty}
                                                                        onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="1"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.unit_price}
                                                                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.discount}
                                                                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        value={Number(item.final_price).toFixed(2)}
                                                                        readOnly
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => removeItem(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="card-title">Payment Details</h6>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Total Amount</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.total_amount}
                                                        readOnly
                                                        style={{ background: '#EEE' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Discount (%)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.discount_percent}
                                                        onChange={(e) => handleDiscountChange(e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Discount Amount</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.discount_amount}
                                                        readOnly
                                                        style={{ background: '#EEE' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label"><strong>Final Amount</strong></label>
                                                    <input
                                                        type="text"
                                                        className="form-control fw-bold"
                                                        value={formData.final_amount}
                                                        readOnly
                                                        style={{ background: '#EEE' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label">Payment Mode</label>
                                                    <select
                                                        className="form-control"
                                                        value={formData.payment_mode}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, payment_mode: e.target.value }))}
                                                    >
                                                        <option value="Cash">Cash</option>
                                                        <option value="Online">Online</option>
                                                        <option value="Card">Card</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-md-8">
                                                <div className="mb-3">
                                                    <label className="form-label">Remarks</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        value={formData.remarks}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                                        placeholder="Enter any remarks..."
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || formData.items.length === 0 || isLoadingBill}
                        >
                            {saving ? 'Saving...' : formData.procedure_bill_id ? 'Update' : 'Save'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark"
                            onClick={generatePDF}
                            disabled={formData.items.length === 0 || isLoadingBill}
                        >
                            Print Bill
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProcedureBillModal;